/**
 * ImageMigrationService - Frontend service for migrating external images to CDN
 *
 * Handles:
 * 1. Fetching external images (may fail due to CORS)
 * 2. Uploading to CDN via StorageService.uploadImage()
 * 3. Manual upload fallback when CORS blocks fetch
 *
 * Used by ImageUrlScannerDialog to migrate external images in CustomScreen HTML.
 */

import { StorageService } from '@modules/firebase-core/services/StorageService';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import type { OnboardingUploadContext } from '@modules/firebase-core/services/OnboardingMediaUploadService';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Result of image migration
 */
export interface MigrationResult {
  /** CDN URL of the uploaded image */
  cdnUrl: string;
  /** Original filename */
  filename: string;
  /** Multi-size asset URLs (origin + small) — present when using WithResize methods */
  assetUrl?: { origin: string; small: string };
}

/**
 * Error thrown when CORS blocks the fetch
 */
export class CorsBlockedError extends Error {
  constructor(url: string) {
    super(`CORS blocked: Cannot fetch ${url}`);
    this.name = 'CorsBlockedError';
  }
}

// ============================================================================
// SERVICE
// ============================================================================

/**
 * ImageMigrationService - Migrate external images to Segmently CDN
 *
 * @example
 * ```typescript
 * const service = new ImageMigrationService();
 *
 * // Try to auto-migrate (may fail due to CORS)
 * try {
 *   const result = await service.migrateFromUrl(externalUrl, context);
 *   console.log('CDN URL:', result.cdnUrl);
 * } catch (error) {
 *   if (error instanceof CorsBlockedError) {
 *     // Show manual upload button
 *   }
 * }
 *
 * // Manual upload (user provides downloaded file)
 * const result = await service.uploadManually(file, originalUrl, context);
 * ```
 */
export class ImageMigrationService {
  private storageService = new StorageService();
  private mediaUploadService = new OnboardingMediaUploadService();

  /**
   * Migrate image from external URL to CDN
   *
   * Attempts to fetch the image directly. May fail due to CORS if the
   * external server doesn't allow cross-origin requests.
   *
   * @param externalUrl - External image URL to migrate
   * @param context - Upload context with onboarding info
   * @returns Promise resolving to CDN URL
   * @throws CorsBlockedError if CORS blocks the fetch
   * @throws Error for other fetch/upload failures
   */
  async migrateFromUrl(
    externalUrl: string,
    context: OnboardingUploadContext
  ): Promise<MigrationResult> {
    // 1. Fetch image directly (may fail due to CORS)
    let response: Response;
    try {
      response = await fetch(externalUrl, {
        mode: 'cors',
        credentials: 'omit', // Don't send cookies
      });
    } catch (error) {
      // Network error or CORS blocked
      throw new CorsBlockedError(externalUrl);
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
    }

    // 2. Validate content type
    const contentType = response.headers.get('content-type') || 'image/png';
    if (!contentType.startsWith('image/')) {
      throw new Error(`Not an image: ${contentType}`);
    }

    // 3. Convert to File
    const blob = await response.blob();
    const filename = this.extractFilename(externalUrl);
    const file = new File([blob], filename, { type: contentType });

    // 4. Upload to CDN
    return this.uploadFile(file, context);
  }

  /**
   * Upload a file manually (when user provides downloaded image)
   *
   * Used as fallback when CORS blocks automatic fetch.
   *
   * @param file - Image file provided by user
   * @param originalUrl - Original external URL (for tracking)
   * @param context - Upload context with onboarding info
   * @returns Promise resolving to CDN URL
   */
  async uploadManually(
    file: File,
    _originalUrl: string, // eslint-disable-line @typescript-eslint/no-unused-vars
    context: OnboardingUploadContext
  ): Promise<MigrationResult> {
    // Validate file is an image
    if (!file.type.startsWith('image/')) {
      throw new Error(`Not an image: ${file.type}`);
    }

    return this.uploadFile(file, context);
  }

  /**
   * Migrate image from external URL with multi-size resizing (origin + small).
   * Uses OnboardingMediaUploadService for automatic size variants.
   */
  async migrateFromUrlWithResize(
    externalUrl: string,
    context: OnboardingUploadContext
  ): Promise<MigrationResult> {
    let response: Response;
    try {
      response = await fetch(externalUrl, { mode: 'cors', credentials: 'omit' });
    } catch {
      throw new CorsBlockedError(externalUrl);
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type') || 'image/png';
    if (!contentType.startsWith('image/')) {
      throw new Error(`Not an image: ${contentType}`);
    }

    const blob = await response.blob();
    const filename = this.extractFilename(externalUrl);
    const file = new File([blob], filename, { type: contentType });

    return this.uploadFileWithResize(file, context);
  }

  /**
   * Upload a file manually with multi-size resizing (origin + small).
   * Fallback for CORS-blocked URLs — user provides the file directly.
   */
  async uploadManuallyWithResize(
    file: File,
    _originalUrl: string,
    context: OnboardingUploadContext
  ): Promise<MigrationResult> {
    if (!file.type.startsWith('image/')) {
      throw new Error(`Not an image: ${file.type}`);
    }
    return this.uploadFileWithResize(file, context);
  }

  /**
   * Internal: Upload file via OnboardingMediaUploadService (multi-size)
   */
  private async uploadFileWithResize(
    file: File,
    context: OnboardingUploadContext
  ): Promise<MigrationResult> {
    const result = await this.mediaUploadService.uploadImage(file, context);
    return {
      cdnUrl: result.assetUrl.origin,
      filename: file.name,
      assetUrl: { origin: result.assetUrl.origin, small: result.assetUrl.small },
    };
  }

  /**
   * Internal: Upload file to CDN
   */
  private async uploadFile(
    file: File,
    context: OnboardingUploadContext
  ): Promise<MigrationResult> {
    // Generate CDN path
    const timestamp = Date.now();
    const sanitizedFilename = this.sanitizeFilename(file.name);
    const cdnPath = `onboardings/${context.onboardingId}/custom-images/${context.locale}_${timestamp}_${sanitizedFilename}`;

    // Upload using existing StorageService
    const result = await this.storageService.uploadImage(cdnPath, file);

    return {
      cdnUrl: result.assetUrl.origin,
      filename: file.name,
    };
  }

  /**
   * Extract filename from URL
   */
  private extractFilename(url: string): string {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      return filename || 'image.png';
    } catch {
      return 'image.png';
    }
  }

  /**
   * Sanitize filename for CDN path
   */
  private sanitizeFilename(filename: string): string {
    // Remove extension first
    const lastDot = filename.lastIndexOf('.');
    const nameWithoutExt = lastDot > 0 ? filename.substring(0, lastDot) : filename;
    const ext = lastDot > 0 ? filename.substring(lastDot) : '';

    // Sanitize name
    const sanitized = nameWithoutExt
      .replace(/\s+/g, '-') // Replace spaces with dashes
      .replace(/[^a-zA-Z0-9\-_]/g, '') // Remove special characters
      .toLowerCase();

    return sanitized + ext;
  }
}

// Export singleton instance for convenience
export const imageMigrationService = new ImageMigrationService();
